<template>
	<Popover
		@open="onOpen"
		@close="onClose"
		:prerender="shouldPrerender"
		v-bind="popoverProps"
		ref="popover"
	>
		<slot />
		<template #content>
			<div class="emoji-picker-container" ref="container">
				<div class="emoji-picker-header">
					<Input
						small
						v-model="search"
						:placeholder="t('common_search')"
						:icon="SearchIcon"
						allowClear
						ref="searchInput"
					/>
				</div>
				<div v-if="!groups.length" class="emoji-picker-skeleton">
					<div class="emoji-picker-skeleton-dots">
						<div v-for="n in 80" :key="n" class="emoji-picker-skeleton-dot" />
					</div>
				</div>
				<IntersectionRoot
					class="emoji-picker-body"
					v-show="!isUserSearching"
					@scroll="onScrollDebounced"
				>
					<IntersectionChild
						class="emoji-picker-group"
						v-for="group in groups"
						:key="group.id"
						@enter="onGroupScrollEnter(group)"
						@leave="onGroupScrollLeave(group)"
					>
						<div
							class="emoji-picker-group-title"
							:data-group-key="group.key"
							v-text="group.label"
						/>
						<div class="emoji-picker-group-emojis">
							<EmojiItem
								v-for="emoji in group.emojis"
								:key="emoji.order"
								:emoji="emoji"
								:groupKey="group.key"
								@select="onSelectEmoji"
								@select-skin="onSelectSkin"
							/>
						</div>
					</IntersectionChild>
				</IntersectionRoot>
				<div
					class="emoji-picker-body search-results"
					v-show="isUserSearching"
					@scroll="onScrollDebounced"
				>
					<div class="emoji-picker-group-emojis">
						<EmojiItem
							v-for="emoji in emojiSearchResults"
							:key="emoji.order"
							:emoji="emoji"
							@select="onSelectEmoji"
							@select-skin="onSelectSkin"
							@contextmenu="onContextMenu"
						/>
					</div>
					<div
						class="emoji-picker-empty-state"
						v-if="!emojiSearchResults.length"
						v-text="t('common_loader_noresults_title')"
					/>
				</div>
				<div v-show="groups.length" class="emoji-picker-toolbar">
					<div
						class="emoji-picker-toolbar-item"
						v-for="group in groups"
						:key="group.id"
						:class="{
							active: !isUserSearching && visibleGroupIds[0] === group.id,
						}"
						@click="onClickToolbarGroup(group)"
					>
						<component :is="group.icon" :title="group.label" type="regular" />
					</div>
				</div>
			</div>
		</template>
	</Popover>
</template>

<script setup>
import {
	ref,
	markRaw,
	useTemplateRef,
	computed,
	nextTick,
	defineComponent,
	provide,
	onMounted,
	onBeforeUnmount,
} from 'vue'
import Input from './Input.vue'
import Popover, { closeAllPopovers } from './Popover.vue'
import {
	IntersectionRoot,
	IntersectionChild,
} from '@/components/common/Intersection'
import {
	getAllEmojiGroups,
	getEmoji,
	getPreferredSkinToneForEmoji,
	getRecentEmojis,
	getSkinsForBaseEmoji,
	searchEmojis,
	setUserSkinPreference,
	addRecentEmoji,
} from '@/helpers/emojis'
import {
	FaceSmileIcon,
	UserIcon,
	BearIcon,
	BowlHotIcon,
	CarIcon,
	LightbulbIcon,
	SymbolsIcon,
	FlagIcon,
	FutbolIcon,
	ClockIcon,
	SearchIcon,
} from '@/icons'
import bus, { useEventBus } from '@/helpers/bus'
import Tippy from './Tippy.vue'
import debounce from 'debounce'
import { useI18n } from 'vue-i18n'
import { hideAll } from 'tippy.js'
const { t } = useI18n()
const RECENTS_GROUP_ID = 100

const emojiIconsByGroupKey = {
	'smileys-emotion': FaceSmileIcon,
	'people-body': UserIcon,
	'animals-nature': BearIcon,
	'food-drink': BowlHotIcon,
	'travel-places': CarIcon,
	activities: FutbolIcon,
	objects: LightbulbIcon,
	symbols: SymbolsIcon,
	flags: FlagIcon,
}

const emit = defineEmits(['open', 'close', 'change'])
const props = defineProps({
	skipSaveRecent: { type: Boolean, default: false },
	popoverProps: { type: Object, default: () => {} },
	// set to a positive number (ms) to prerender emojis after a delay
	// prerendering is disabled by default to avoid memory leaks from
	// 1400+ emoji DOM nodes being teleported to body per instance
	prerenderDelay: { type: Number, default: 0 },
})
defineExpose({
	openPicker,
	closePicker,
})

const groups = ref([])
const search = ref('')
const visibleGroupIds = ref([])
const container = useTemplateRef('container')
const popover = useTemplateRef('popover')
const ignoreScroll = ref(false)
const shouldPrerender = ref(false)
const searchInput = useTemplateRef('searchInput')
const debouncedOnScroll = debounce(onScroll, 25)
const onScrollDebounced = () => {
	hideAll({ duration: 0 })
	debouncedOnScroll()
}

let prerenderTimeout = null

onMounted(() => {
	if (props.prerenderDelay > 0) {
		prerenderTimeout = setTimeout(() => {
			updateEmojis()
			shouldPrerender.value = true
		}, props.prerenderDelay)
	}
})

onBeforeUnmount(() => {
	if (prerenderTimeout) clearTimeout(prerenderTimeout)
})

useEventBus('emojis-update', updateEmojis)
useEventBus('recent-emojis-updated', updateEmojis)

const isUserSearching = computed(() => !!search.value.length)
const emojiSearchResults = computed(() => {
	if (!search.value.length) return []
	return searchEmojis(search.value)
})
const popoverId = computed(() => popover.value?.id || null)
provide('parentPopoverId', popoverId)

function onOpen() {
	emit('open', true)
	ignoreScroll.value = true
	setTimeout(() => {
		updateEmojis()
		ignoreScrollTemporarily()
		searchInput.value?.focus()
	}, 1)
}

function onClose() {
	emit('close', false)
	groups.value = []
	search.value = ''
}

function onContextMenu(e, emoji) {
	e.preventDefault()
	alert('context menu', emoji)
}

function selectGroup(group) {
	visibleGroupIds.value = [group.id, ...visibleGroupIds.value]
}

function onClickToolbarGroup(group) {
	search.value = ''
	nextTick(() => {
		selectGroup(group)
		scrollToGroup(group)
	})
}

function onGroupScrollEnter(group) {
	if (ignoreScroll.value) return
	selectGroup(group)
}

function onGroupScrollLeave(group) {
	if (ignoreScroll.value) return
	visibleGroupIds.value = visibleGroupIds.value.filter((id) => id !== group.id)
}

function scrollToGroup(group) {
	const groupElement = container.value?.querySelector(
		`[data-group-key="${group.key}"]`
	)
	if (groupElement) {
		// TODO: decide if we want 'smooth' or 'instant' scrolling
		groupElement.scrollIntoView({ behavior: 'smooth' })
		ignoreScrollTemporarily() // remove this if instant scrolling is desired
	}
}

function onScroll() {
	closeAllPopovers([popover.value.id])
}

function ignoreScrollTemporarily(ms = 500) {
	// while the UI is first rendering or while we're animating the scroll
	// between groupings, the scroll events can cause weird behavior with the
	// grouping highlights, so we just ignore them during those periods
	ignoreScroll.value = true
	setTimeout(() => {
		ignoreScroll.value = false
	}, ms)
}

function openPicker() {
	popover.value.openPopover()
}

function closePicker() {
	search.value = ''
	popover.value.closePopover()
}

function onSelectEmoji(emoji) {
	emit('change', emoji)
	if (!props.skipSaveRecent) {
		addRecentEmoji(emoji.emoji)
	}
	closePicker()
}

function onSelectSkin(skin) {
	if (typeof skin === 'string') {
		skin = getEmoji(skin)
	}
	setUserSkinPreference(skin)
	emit('change', skin)
	closePicker()
}

function updateEmojis() {
	groups.value = getAllEmojiGroups().map((group) => ({
		...group,
		icon: markRaw(emojiIconsByGroupKey[group.key]),
	}))
	const recentEmojis = getRecentEmojis()
	if (recentEmojis.length) {
		groups.value = [
			{
				id: RECENTS_GROUP_ID,
				key: 'recents',
				label: t('emoji_picker_recently_used'),
				icon: markRaw(ClockIcon),
				emojis: recentEmojis,
			},
			...groups.value,
		]
	}
	if (!visibleGroupIds.value.length) {
		visibleGroupIds.value = recentEmojis.length
			? [RECENTS_GROUP_ID, groups.value[1].id]
			: [groups.value[0].id]
	}
}

const EmojiItem = defineComponent({
	components: { Tippy, Popover },
	props: {
		emoji: {
			type: Object,
			required: true,
		},
		groupKey: {
			type: String,
			required: false,
			default: null,
		},
	},
	emits: ['select', 'select-skin', 'mounted'],
	inject: ['parentPopoverId'],
	mounted() {
		this.$emit('mounted')
	},
	data() {
		return {
			showSkinTonePopover: false,
		}
	},
	computed: {
		tippyContent() {
			if (this.emoji.hasSkins && !this.showSkinTonePopover) {
				return this.$t('emoji_picker_customize_tooltip')
			}
			return null
		},
		emojiToDisplay() {
			if (this.groupKey === 'recents') return this.emoji
			if (!this.emoji.hasSkins) return this.emoji
			return getPreferredSkinToneForEmoji(this.emoji.emoji) || this.emoji
		},
		skins() {
			if (!this.emoji.hasSkins) return []
			if (this.groupKey === 'recents')
				return getSkinsForBaseEmoji(this.emoji.baseEmoji) || []
			return getSkinsForBaseEmoji(this.emoji.emoji) || []
		},
	},
	methods: {
		openSkinTonePopover(e) {
			if (!this.emoji.hasSkins) return
			e.preventDefault()
			e.stopPropagation()
			this.showSkinTonePopover = true
		},
		closeSkinTonePopover(e) {
			e.stopPropagation()
			this.showSkinTonePopover = false
		},
		onPopoverClose() {
			this.showSkinTonePopover = false
		},
	},
	template: `
        <Tippy
            class="emoji-picker-emoji"
            :class="{ active: emoji.hasSkins && showSkinTonePopover }"
            :content="tippyContent"
            :options="{
                arrow: true,
                maxWidth: '134px',
                delay: [500, 0],
                textAlign: 'center',
            }"
        >
            <div
                class="emoji-picker-emoji-content"

            >
                <Popover
                    v-if="emoji.hasSkins && showSkinTonePopover"
                    :keepOpenIds="[parentPopoverId]"
                    contentClass="emoji-picker-skin-tone-popover"
                    top
                    centered
                    open
                    :offsetY="-8"
                    @close="onPopoverClose"
                    ref="skins"
                >
                    <span
                        v-text="emojiToDisplay.emoji"
                        @click="closeSkinTonePopover"
						class="emoji-char"
                    />
                    <template #content>
                        <div
                            class="emoji-picker-skin-tone-base emoji-picker-skin-tone-item"
                            v-text="emoji.baseEmoji"
                            @click="$emit('select-skin', emoji.baseEmoji)"
                        />
                        <div class="emoji-picker-skin-tone-divider" />
                        <div class="emoji-picker-skin-tones">
                            <div
                                class="emoji-picker-skin-tone-item"
                                v-for="skin in skins"
                                :key="skin.emoji"
                                v-text="skin.emoji"
                                @click="$emit('select-skin', skin)"
                            />
                        </div>
                    </template>
                </Popover>
                <span
                    v-if="!showSkinTonePopover"
                    v-text="emojiToDisplay.emoji"
                    @click="$emit('select', emojiToDisplay)"
                    @contextmenu="openSkinTonePopover"
                />
            </div>
        </Tippy>
    `,
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

@keyframes skeleton {
	to {
		background-position: 250% 0;
	}
}

.emoji-picker-container {
	width: 320px;
	height: 445px;
	background-color: var(--neutral-0);
	@include radius;
	box-shadow: 0 4px 34px 0 rgba(0, 0, 0, 0.65);
	@include flex-column;
	position: relative;

	.emoji-picker-header {
		padding-bottom: 16px;
		padding: 16px;
	}

	.emoji-picker-body {
		overflow-x: hidden;
		overflow-y: auto;
		flex-grow: 1;
		padding: 0 16px 16px 16px;

		.emoji-picker-group {
			margin-bottom: 8px;

			&:not(:first-child) {
				margin-top: 16px;
			}

			.emoji-picker-group-title {
				font-size: 12px;
				color: var(--neutral-600);
				text-transform: capitalize;
				margin-bottom: 4px;
			}
		}
		.emoji-picker-group-emojis {
			display: grid;
			grid-template-columns: repeat(8, 1fr);
			row-gap: 4px;
			margin-left: -4px;
			margin-right: -4px;
			.emoji-picker-emoji {
				font-size: 28px;
				padding: 0 4px;
				width: 36px;
				height: 34px;
				@include radius;
				cursor: pointer;
				&:hover,
				&.active {
					background: var(--neutral-100);
				}
			}
		}
		.emoji-picker-empty-state {
			height: 100%;
			width: 100%;
			@include flex-center;
			font-size: 14px;
			color: var(--neutral-600);
		}
	}

	.emoji-picker-skeleton {
		flex-grow: 1;
		padding: 0 16px 16px 16px;
		overflow: hidden;
	}
	.emoji-picker-skeleton-dots {
		display: grid;
		grid-template-columns: repeat(8, 1fr);
		row-gap: 4px;
		justify-items: center;
	}
	.emoji-picker-skeleton-dot {
		width: 28px;
		height: 28px;
		border-radius: 50%;
		background-color: rgba(#e5e6ea, 0.5);
		background-image: linear-gradient(
			90deg,
			rgba(lightgrey, 0) 0,
			rgba(lightgrey, 0.6) 50%,
			rgba(lightgrey, 0) 100%
		);
		background-size: 50% 100%;
		background-position: -100% 0;
		background-repeat: no-repeat;
		animation: skeleton 2s ease-in-out infinite;
	}

	.emoji-picker-toolbar {
		@include flex;
		flex-shrink: 0;
		justify-content: space-between;
		height: 40px;
		padding: 0 16px;
		border-top: 1px solid var(--neutral-100);
		width: 100%;

		.emoji-picker-toolbar-item {
			@include flex-center;
			--size: 28px;
			font-size: 16px;
			width: var(--size);
			height: var(--size);
			cursor: pointer;
			color: var(--neutral-600);
			@include radius;
			&.active {
				background: var(--neutral-100);
				color: var(--teal-300);
			}
			&:hover {
				background: var(--neutral-100);
			}
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.emoji-picker-skin-tone-popover {
	@include flex(4px);
	align-items: flex-start;
	max-width: 300px;
	@include radius;
	box-shadow: 0 4px 34px 0 rgba(0, 0, 0, 0.35);
	background-color: var(--neutral-0);
	padding: 8px;
	.emoji-picker-skin-tone-divider {
		height: 32px;
		border-right: 1px solid var(--neutral-200);
	}
	.emoji-picker-skin-tones {
		display: grid;
		grid-template-columns: repeat(5, 1fr);
		row-gap: 4px;
	}
	.emoji-picker-skin-tone-item {
		@include flex-center;
		font-size: 28px;
		padding: 0 4px;
		width: 36px;
		height: 34px;
		@include radius;
		cursor: pointer;
		&:hover {
			background: var(--neutral-100);
		}
	}
}

.popover-trigger {
	.emoji-char {
		user-select: none;
		-webkit-user-select: none;
	}
}

@include overrideScheme('dark') {
	.emoji-picker-skeleton-dot {
		background-color: rgba(#4e4e4f, 0.5);
		background-image: linear-gradient(
			90deg,
			rgba(darkgray, 0) 0,
			rgba(darkgray, 0.6) 50%,
			rgba(darkgray, 0) 100%
		);
	}
}
</style>
